import { createUserWithEmailAndPassword, getAuth, signInWithCustomToken, signInWithEmailAndPassword, updateProfile, signOut } from 'firebase/auth'
import { useSwrCache } from '~/composables/useSwrCache'

// //////////////////////////////////////////////////////////////////
// 💬 Create Application User
// //////////////////////////////////////////////////////////////////
// Create User
export const createUser = async (email: string, password: string) => {
  const auth = getAuth()
  try {
    return await createUserWithEmailAndPassword(auth, email, password)
  }
  catch (error: any) {
    const errorCode = error.code
    const errorMessage = error.message

    return `Failed Creating User: ${email} | ${errorCode} ${errorMessage}`
  }
}

// //////////////////////////////////////////////////////////////////
// 💬 Logout User
// //////////////////////////////////////////////////////////////////
// User Logout
export const userLogout = async () => {
  const { $user } = useNuxtApp()
  const router = useRouter()
  const auth = getAuth()

  try {

    // 💬 Clear Local Cookies and Storage
    //
    // Storage hygiene on logout splits into TWO buckets:
    //
    //   Cleared (session state — must not bleed into next user):
    //     - user cookie     → Firebase Auth's session marker
    //     - snowflakeAuth   → Snowflake OAuth tokens
    //     - defaultLogin    → cached account-login-options snapshot
    //                         (login methods, OAuth integration, etc.)
    //                         from the last login flow
    //
    //   Preserved (convenience — should follow the device, not the
    //   user — login form pre-fills it on next visit):
    //     - pulsarLastEmail → the email of whoever just logged out, so
    //                         /login can pre-fill it without exposing
    //                         account snapshot or methods. Cleared
    //                         only when explicitly switching users via
    //                         a "Forget me on this device" toggle
    //                         (future feature).
    //
    // Plus a session-scoped `pulsarExplicitLogout` flag so the dev
    // auto-login on /login doesn't immediately re-sign-in the user
    // they just signed out. The flag is one-shot — consumed on the
    // next /login mount.
    const userCookie = useCookie('user', { maxAge: 180 * 60 })
    const snowflakeAuth = useGetLocalStorage('snowflakeAuth')

    // Capture the email BEFORE signOut (auth.currentUser is null
    // afterwards). Empty string if we somehow got here without an
    // active user — the login page treats empty as "no preference."
    const emailToRemember = auth.currentUser?.email || ''

    userCookie.value = null
    if (snowflakeAuth) useSetLocalStorage('snowflakeAuth', JSON.stringify({}))
    if (import.meta.client) {
      try {
        localStorage.removeItem('defaultLogin')
        if (emailToRemember) {
          localStorage.setItem('pulsarLastEmail', emailToRemember)
        }
        sessionStorage.setItem('pulsarExplicitLogout', '1')

        // Clear every cached dashboard payload (cost analytics, future
        // dashboards, etc). Without this, a different user signing in on
        // the same browser would briefly see the previous user's numbers
        // before the background refresh fires. The cache helper iterates
        // its own `pulsar:` namespace, so we don't touch unrelated keys.
        useSwrCache().clearAll()
      } catch (e) {
        console.warn('Could not update logout-side storage:', e)
      }
    }

    const authStore = useAuthStore()
    authStore.clearUser()

    // 💬 Firebase Sign Out
    await signOut(auth)

    // 💬 Reroute to Login Page
    router.push({ path: '/login' })

    // 💬 Unsub From Docs and Remove Doc Subscriptions
    const { docs } = storeToRefs(useDocStore())
    if (docs.value) {
      const keys = Object.keys(docs.value)
      keys.forEach((key) => {
        const doc = docs.value[key]
        if (doc?.unsub && typeof doc.unsub === 'function')
          doc.unsub()
        delete docs.value[key]
      })
    }

    // 💬 Clear Sensitive Data Stores. Includes the non-OAuth bearer
    // cache (decrypted PAT, imported keypair CryptoKey, current
    // signed JWT) — these would otherwise leak across sessions if
    // a different user signs in on the same browser.
    const snowflakeAuthStore = useSnowflakeAuthStore()
    const { snowflakeCredentials } = storeToRefs(snowflakeAuthStore)
    snowflakeCredentials.value = {} as any
    snowflakeAuthStore.clearNonOauthCache()

    // 🔔 Tear down the notifications listener so the next user signing
    // in on this browser doesn't inherit the previous account's bell
    // contents during the auth-state transition window.
    useNotificationsStore().teardown()
  }
  catch (error: any) {
    console.error('userLogout error:', error)
    const errorCode = error.code
    const errorMessage = error.message

    return `Failed To Logout User | ${errorCode} ${errorMessage}`
  }
}

// //////////////////////////////////////////////////////////////////
// 💬 User Login - Password or Custom Token Authentication
// //////////////////////////////////////////////////////////////////
export const userLogin = async (email: string, password: string | null, customToken: string | null, isSnowflakeAuth: boolean = false): Promise<any | string> => {
  const { snowflakeCredentials } = storeToRefs(useSnowflakeAuthStore())
  const auth = getAuth()
  const router = useRouter()
  try {
    const loginUser = password ? await signInWithEmailAndPassword(auth, email, password) : customToken ? await signInWithCustomToken(auth, customToken) : null
    if (!loginUser)
      return null

    // 💬 Get Account Details From Firebase.
    //
    // accountId comes from the user's custom claims, set during
    // /register by the app_register_account Cloud Function. Users
    // without a claim get routed to /register by the auth middleware
    // (defensive — a healthy signup always lands the claim atomically).
    // displayName is the user's actual name (first + last) per Firebase
    // convention.
    const user = ref({} as any)
    user.value = loginUser.user

    // 💬 Force a token refresh on the custom-token signup path.
    //
    // app_register_account calls auth.set_custom_user_claims BEFORE
    // minting the custom token, so claims SHOULD be in the ID token
    // produced by signInWithCustomToken. But Firebase's claim
    // propagation has a small async window, and a forced refresh
    // costs <100ms — cheap insurance against a "logged in but no
    // accountId" race that would otherwise bounce the user to
    // /register on first navigation.
    if (customToken) {
      try { await loginUser.user.getIdToken(true) }
      catch (e) { console.warn('Forced token refresh failed:', e) }
    }

    const userId = user.value.uid

    // 💬 Set If Snowflake Is Authenticating The Application
    // If Snowflake is Unavailable User Is Logged Out
    snowflakeCredentials.value.appAuthenticatedWithSnowflake = isSnowflakeAuth

    // 💬 Update Snowflake Credentials on Firestore.
    //
    // Awaited so the user doc is consistent before downstream code
    // (StatusIndicator backend probe via app_snowflake_session, etc.)
    // tries to read the refresh token. Previously this was fire-and-
    // forget, which produced a "no stored refresh token" race for
    // ~500ms after a fresh OAuth login.
    //
    // Note for fresh signups: register_account.py also writes the
    // refresh token atomically as part of doc creation (passed
    // through the registration payload), so this write is mostly
    // a no-op delta on signup — but it keeps refresh-token rotations
    // on subsequent OAuth logins working via the same path.
    if (snowflakeCredentials.value.refreshTokenEncrypted) {
      try {
        await useSetDoc('users', userId, { snowflakeConnections: { 1: { refreshToken: snowflakeCredentials.value.refreshTokenEncrypted, refreshTokenExpiresAt: snowflakeCredentials.value.refreshTokenExpiresAt } } })
      } catch (e) {
        console.warn('Could not persist refresh token to user doc:', e)
      }
    }

    // 💬 Write Snowflake Authentication to LocalStorage
    // For Persistence On Browser Refresh
    // /////////////////////////////////////////////////
    useSetLocalStorage('snowflakeAuth', JSON.stringify(snowflakeCredentials.value))

    // 💬 Sync Profile and Account
    const globalsStore = useGlobalsStore()
    const { userId: globalUserId, accountId: globalAccountId } = storeToRefs(globalsStore)

    globalUserId.value = userId

    // Pull accountId off the JWT custom claim. Healthy signups via
    // /register land the claim atomically; a missing claim here means
    // either (a) corrupted partial signup state, or (b) the claim
    // propagation race won (rare — we forceRefresh above on the
    // custom-token path). The auth middleware will route to /register
    // if accountId stays null.
    try {
      const tokenResult = await loginUser.user.getIdTokenResult()
      const claimAccountId = (tokenResult.claims?.accountId as string | undefined) ?? null
      if (claimAccountId) {
        globalAccountId.value = claimAccountId
        globalsStore.syncAccountProfile(claimAccountId)
        globalsStore.syncUserProfile(claimAccountId, userId)
      }
    }
    catch (e) {
      console.error('Failed to read accountId claim during login:', e)
    }

    // 💬 Ensure cookie and store are set before we push route
    const userCookie = useCookie('user', { maxAge: 180 * 60 })
    userCookie.value = JSON.stringify({ uid: loginUser.user.uid, email: loginUser.user.email })

    // Set user explicitly in store
    const authStore = useAuthStore()
    authStore.setUser(loginUser.user)

    // 💬 Reroute to Main Page
    router.push({ path: '/' })

    return loginUser.user
  }
  catch (error: any) {
    const errorCode = error.code
    const errorMessage = error.message

    return { error: `Failed To Login In User: ${email} | ${errorCode} ${errorMessage}` }
  }
}

// //////////////////////////////////////////////////////////////////
// 💬 Update Firebase Auth
// //////////////////////////////////////////////////////////////////
export const updateFirebaseUser = async (payload: any) => {
  const auth = getAuth()
  try {
    if (auth.currentUser) {
      await updateProfile(auth.currentUser, payload)
      console.log('User Was Updated')
    }
    else {
      throw new Error('No authenticated user found')
    }
  }
  catch (error: any) {
    const errorCode = error.code
    const errorMessage = error.message

    return `Failed To Update User | ${errorCode} ${errorMessage}`
  }
}

// //////////////////////////////////////////////////////////////////
// 💬 Getting Custom Token Claims
// //////////////////////////////////////////////////////////////////
export const getUsersCustomTokenClaims = async () => {
  console.log('Getting Custom Claims For User')

  const { $auth } = useNuxtApp()
  const user = $auth.currentUser
  const tokenResults = user ? await user.getIdTokenResult() : null

  return tokenResults ? tokenResults.claims : {}
}
